import produce from 'immer';
import { useCallback } from 'react';
import { InfiniteData, useInfiniteQuery, useMutation } from 'react-query';
import message from 'antd/es/message';

import { streamActionToast } from '@/components/SystemErrorToast';
import { queryClient, request, useCurrentUserId, toastError } from '@/utils';
import { FeedArticleEntity } from '@/views/Stream/types';

import { STREAM_TRIAL_LIMIT_CODE } from '../const';
import { transComment } from '../utils';
import { setQueryAllFeedDetailByArticleId } from './setQuery';
import { useCommentOrder } from '../state';
import { FeedArticleDetail, FeedComment, CommentOrderBy } from '../types';

export const COMMENTS_PAGE_LIMIT = 20;

export const useArticleComments = ({
  id,
  orderBy,
  isLowQuality = false,
}: {
  id: number;
  orderBy: string;
  isLowQuality?: boolean;
}) => {
  const currentAddress = useCurrentUserId();
  const url = '/article/comments';

  const select = useCallback(
    (data: InfiniteData<{ comments: FeedComment[]; total_count: number }>) => {
      return produce(data, draft => {
        draft?.pages?.forEach(page => {
          page?.comments?.forEach(transComment);
        });
      });
    },
    [],
  );

  return useInfiniteQuery(
    [url, currentAddress, orderBy, id, { isLowQuality }],
    ({ pageParam = 0 }) => {
      const params = {
        id,
        start: pageParam,
        limit: COMMENTS_PAGE_LIMIT,
        order_by: orderBy,
      };
      if (isLowQuality) {
        Object.assign(params, { is_low_quality: isLowQuality });
      }
      return request.get<{ comments: FeedComment[]; total_count: number }>(url, params);
    },
    {
      getNextPageParam: (lastPage, pages) => {
        const length = lastPage.comments?.length;
        if (length >= COMMENTS_PAGE_LIMIT) {
          return pages.length * COMMENTS_PAGE_LIMIT;
        }
      },
      select,

      onError(err: any) {
        toastError(err);
      },
    },
  );
};

export const invalidArticleComments = (address?: string) => {
  queryClient.invalidateQueries(['/article/comments', address]);
};

export const useFeedCommentAdd = (id: number) => {
  const currentAddress = useCurrentUserId();
  const [commentOrderBy] = useCommentOrder();

  return useMutation(
    (params: {
      content: string;
      reply_to_comment_id?: number;
      contributor_id?: string;
      image_ids?: string[];
      entities?: FeedArticleEntity;
    }) => {
      return request.post<{ is_success: boolean; comment: FeedComment }>('/article/comment', {
        id,
        ...params,
      });
    },
    {
      onSuccess(res) {
        if (!res?.comment) {
          message.error('Failed');

          return;
        } else {
          message.success('Added');
        }

        const comment = {
          ...res.comment,
          trust_count: 0,
          distrust_count: 0,
        };

        const recipe = (pre?: FeedArticleDetail) => {
          // timeline 中的 comments，现在是 pinned by author，本地不用主动添加
          // if (pre?.comments) {
          //   pre.comments.push(comment);
          // }

          if (pre?.article) {
            pre.article.comment_count = (pre.article.comment_count || 0) + 1;
          }
        };

        setQueryAllFeedDetailByArticleId({
          currentAddress: currentAddress!,
          articleId: id,
          recipe,
        });

        queryClient.setQueriesData<
          InfiniteData<{ comments: FeedComment[]; total_count: number }> | undefined
        >(
          ['/article/comments', currentAddress, commentOrderBy, id, { isLowQuality: false }],
          pre => {
            if (!pre) {
              return pre;
            }

            return produce(pre, draft => {
              const pageLength = draft.pages.length;
              const lastPage = pageLength > 0 ? draft.pages[pageLength - 1] : undefined;
              const lastPageCount = lastPage?.comments.length;
              const totalCount = lastPage?.total_count;

              if (lastPageCount && lastPageCount < COMMENTS_PAGE_LIMIT) {
                // 最后一页还有余位，直接插到最后一页中
                draft.pages[pageLength - 1].comments.push(comment);
                lastPage.total_count += 1;
              } else {
                // 没位置了，新建一页
                draft.pages.push({
                  comments: [comment],
                  total_count: (totalCount || 0) + 1,
                });
              }
            });
          },
        );
      },
      onError(err: any) {
        toastError(err || 'Failed');
      },
    },
  );
};

const changeTrustState = (pre?: FeedComment, is_trust?: boolean | null) => {
  if (pre) {
    const [isTrust, isDistrust] = [pre.is_trust, pre.is_trust === false];

    if (is_trust) {
      pre.trust_count = (pre.trust_count || 0) + 1;
      if (isDistrust) {
        pre.distrust_count = pre.distrust_count > 1 ? pre.distrust_count - 1 : 0;
      }
    } else if (is_trust === false) {
      pre.distrust_count = (pre.distrust_count || 0) + 1;

      if (isTrust) {
        pre.trust_count = pre.trust_count > 1 ? pre.trust_count - 1 : 0;
      }
    } else {
      // null，取消 trust 或者 distrust
      if (isTrust) {
        pre.trust_count = pre.trust_count > 1 ? pre.trust_count - 1 : 0;
      } else if (isDistrust) {
        pre.distrust_count = pre.distrust_count > 1 ? pre.distrust_count - 1 : 0;
      }
    }

    pre.is_trust = is_trust!;

    return pre;
  }
};

const changeTrustData = (
  id: number,
  commentOrderBy: CommentOrderBy,
  isTrust: boolean | null,
  currentAddress?: string,
) => {
  queryClient.setQueriesData<
    InfiniteData<{ comments: FeedComment[]; total_count: number }> | undefined
  >(['/article/comments', currentAddress, commentOrderBy], pre => {
    if (!pre) {
      return pre;
    }
    return produce(pre, draft => {
      draft?.pages?.find(x => {
        const record = x.comments?.find((p: FeedComment) => p.id === id);
        if (record) {
          changeTrustState(record, isTrust);
          return true;
        }
        return false;
      });
    });
  });
};

// trust/distrust comment
export const useCommentTrust = (id: number, oldTrust: boolean | null) => {
  const currentAddress = useCurrentUserId();
  const [commentOrderBy] = useCommentOrder();
  let isTrustAction: boolean;
  return useMutation(
    (is_trust: boolean | null) => {
      changeTrustData(id, commentOrderBy, is_trust, currentAddress);
      isTrustAction = !!is_trust;
      return request.post<{ is_success: boolean }>('/article_comment/trust', {
        id,
        is_trust,
      });
    },
    {
      onSuccess(res, is_trust) {
        if (!res?.is_success) {
          message.error('Failed');

          changeTrustData(id, commentOrderBy, oldTrust, currentAddress);
        }
      },
      onError(err: any) {
        if (err?.error_code === STREAM_TRIAL_LIMIT_CODE) {
          streamActionToast(!!isTrustAction ? 'trust' : 'distrust');
        } else {
          toastError(err || 'Failed');
        }
        changeTrustData(id, commentOrderBy, oldTrust, currentAddress);
      },
    },
  );
};
